import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, CarFront } from "lucide-react";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#o-nas", label: "O nas" },
    { href: "#oferta", label: "Oferta" },
    { href: "#dlaczego-my", label: "Dlaczego my" },
    { href: "#opinie", label: "Opinie" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md border-b border-slate-200" : "bg-white/95 backdrop-blur-sm"
      }`}
      data-testid="navigation"
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center group-hover:bg-yellow-400 transition-colors">
              <CarFront className="w-7 h-7 text-yellow-400 group-hover:text-slate-900 transition-colors" />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-xl text-slate-900 tracking-tight block leading-tight">
                SMRECZAK
              </span>
              <span className="text-xs text-slate-500 font-medium tracking-wide">
                OSK NIEDŹWIEDŹ
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm tracking-wider"
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:608490104"
              className="flex items-center gap-2 text-slate-700 hover:text-slate-900 font-semibold transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-5 h-5 text-yellow-500" />
              <span className="font-heading tracking-wide">608 490 104</span>
            </a>
            <button
              onClick={() => scrollToSection("#kontakt")}
              className="btn-primary text-sm py-3 px-6"
              data-testid="nav-cta-button"
            >
              Zapisz się
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-900"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label={isMobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 border-t border-slate-100">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => scrollToSection(link.href)}
                    className="block w-full text-left py-3 px-4 font-heading font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-colors tracking-wide"
                    data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </motion.button>
                ))}
                <div className="mt-4 pt-4 border-t border-slate-100 px-4">
                  <a
                    href="tel:608490104"
                    className="flex items-center gap-2 text-slate-700 font-semibold mb-4"
                    data-testid="mobile-phone"
                  >
                    <Phone className="w-5 h-5 text-yellow-500" />
                    <span className="font-heading tracking-wide">608 490 104</span>
                  </a>
                  <button
                    onClick={() => scrollToSection("#kontakt")}
                    className="btn-primary w-full text-sm py-3"
                    data-testid="mobile-cta-button"
                  >
                    Zapisz się na kurs
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navigation;
